import * as React from 'react';
import { isSupportedFile, pause, readJSONFile } from '../utils';

import { getStoreState, storeDispatch } from '../state/Store';
import * as Actions from '../state/actions';
import { isSettingFile } from '../utils/index';
import { FlyerData } from 'data/FlyerData';
import Layer from 'data/Layer';
import { matchResolutionWithStandards } from 'data/Resolution';

const DRAG_NONE = 'DRAG_NONE';
const DRAG_OK = 'DRAG_OK';
const DRAG_BAD = 'DRAG_BAD';

const isDraggingUrl = (items: DataTransferItemList): boolean => {
  for (let i = 0; i < items.length; i++) {
    const item = items[0];
    if (item.kind === 'string' && item.type === 'text/uri-list') {
      return true;
    }
  }
  return false;
};

interface Props {
  className: string;
}

export default class extends React.Component<Props> {
  state = {
    drag: DRAG_NONE
  };
  didDragOver = (e: React.DragEvent<HTMLElement>) => {
    e.dataTransfer.dropEffect = 'copy';
    const items = e.dataTransfer.items;

    let drag = DRAG_OK;
    let audioCount = 0;
    let settingsCount = 0;

    if (isDraggingUrl(items)) {
      // dragging image from web page
    } else {
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === 'file') {
          if (isSupportedFile(item)) {
            const type = item.type.split('/')[0];
            if (type === 'audio') {
              audioCount++;
              if (audioCount > 1) {
                drag = DRAG_BAD;
                break;
              }
            } else if (isSettingFile(item)) {
              settingsCount++;
              if (settingsCount > 1) {
                drag = DRAG_BAD;
                break;
              }
            }
          } else {
            drag = DRAG_BAD;
            break;
          }
        } else {
          drag = DRAG_BAD;
          break;
        }
      }
    }

    this.setState({
      drag
    });

    e.preventDefault();
  };

  didDrop = async (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();

    this.setState({
      drag: DRAG_NONE
    });

    console.log('DROP');

    let droppedUrl = e.dataTransfer.getData('URL');
    if (droppedUrl) {
      storeDispatch(Actions.addRemoteGraphics(droppedUrl));
      return;
    }

    const files = Array.from(e.dataTransfer.files);

    const unsupportedFiles = files.filter(f => !isSupportedFile(f));
    if (unsupportedFiles.length > 0) {
      alert('You cannot drag unsupported files');
      return;
    }

    const audioFiles = files.filter(f => f.type.split('/')[0] === 'audio');
    if (audioFiles.length > 1) {
      alert('You cannot drag more than 1 audio file');
      return;
    }

    const settingFiles = files.filter(f => isSettingFile(f));
    if (settingFiles.length > 1) {
      alert('You cannot drag more than 1 settings file');
      return;
    }

    const images = files.filter(
      f => !audioFiles.includes(f) && !settingFiles.includes(f)
    );

    images.sort((a: File, b: File) => {
      if (a.name < b.name) {
        return -1;
      } else if (a.name === b.name) {
        return 0;
      }
      return 1;
    });

    if (settingFiles.length > 0) {
      this.addImagesWithSettingsFile(images, settingFiles[0]);
    }

    if (audioFiles.length > 0) {
      storeDispatch(Actions.addAudioFile(audioFiles[0]));
      await pause(1);
    }
    if (settingFiles.length > 0) {
      return;
    }
    storeDispatch(Actions.addLocalGraphics(images));
  };

  async addImagesWithSettingsFile(images: File[], settingsFile: File) {
    const settings = (await readJSONFile(settingsFile)) as FlyerData;
    if (settings) {
      if (settings.layers && settings.layers.length) {
        this.addImagesWithSettings(images, settings);
      } else {
        alert("The setting file doesn't seem to contain any layer");
      }
    } else {
      alert('There was an error parsing the Settings file');
    }
  }

  async addImagesWithSettings(images: File[], settings: FlyerData) {
    const getImageURLWithName = (name: string): string | null => {
      for (var i = 0; i < images.length; i++) {
        const img = images[i];
        if (img.name === name) {
          return URL.createObjectURL(img);
        }
      }
      return null;
    };
    settings.layers!.forEach((layerData: any) => {
      const frames = layerData.frames;
      for (var i = 0; i < frames.length; i++) {
        const url = getImageURLWithName(frames[i].name);
        if (url) {
          frames[i].url = url;
        } else {
          // removed not matching image
          console.log("Couldn't find image with name: " + frames[i].name);
          frames.splice(i, 1);
          i--;
        }
      }
    });
    const { flyer } = getStoreState();
    let resolution = flyer.resolution;
    if (
      resolution.width !== settings.resolution.width ||
      resolution.height !== settings.resolution.height
    ) {
      storeDispatch(
        Actions.changeResolution(
          matchResolutionWithStandards(settings.resolution) ??
            settings.resolution
        )
      );
      resolution = settings.resolution;
    }
    const { previewPixelRatio } = flyer;
    const layers: Layer[] = [];
    settings.layers!.forEach((layerData: any) => {
      if (layerData.frames.length) {
        const layer = new Layer();
        layer.populateFromObj({
          ...layerData,
          stageWidth: resolution.width * previewPixelRatio.value,
          stageHeight: resolution.height * previewPixelRatio.value
        });
        layers.push(layer);
      }
    });
    for (const layer of layers) {
      await layer.load();
    }
    storeDispatch(Actions.addLayers(layers));
  }

  didDragOut = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();

    this.setState({
      drag: DRAG_NONE
    });
  };

  render() {
    let className = this.props.className;
    if (this.state.drag === DRAG_OK) {
      className += ' drag-ok';
    } else if (this.state.drag === DRAG_BAD) {
      className += ' drag-bad';
    }
    return (
      <div
        className={className}
        onDrop={this.didDrop}
        onDragOver={this.didDragOver}
        onDragLeave={this.didDragOut}
        onClick={() => {
          // ios hack: https://stackoverflow.com/a/39712411/326074
        }}
      >
        {this.props.children}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/DropComponent.tsx